// InteractiveBoothMap Component (Issue #676, #749, #744, #1159)
// Interactive SVG map with drag, zoom, pan for booth visualization
// Mobile touch gesture support added in Issue #749
// Accessibility improvements added in Issue #744 (WCAG 2.1 AA)
// Vendor search and filtering added in Issue #1159 (Customer-centric features)

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import AmenityMarker from './AmenityMarker';
import BoothMapLegend from './BoothMapLegend';
import BoothDetailsModal from './BoothDetailsModal'; // Issue #1159: Customer booth details modal
import { BoothLayout, BoothAmenity, BoothMapSlot } from '../types';
import {
  mockBoothLayouts,
  mockBoothAmenities,
  mockSlotsWithFeatures,
} from '../mockData/boothMapData';
import { vibrateSuccess, vibrateLight, vibrateNotification } from '../utils/haptics';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'; // Issue #744: Reduced motion support
import { useDebounce } from '../hooks/useDebounce'; // Issue #1218: Debounce vendor search

interface InteractiveBoothMapProps {
  fairId?: number;
  layouts?: BoothLayout[];
  amenities?: BoothAmenity[];
  slots?: BoothMapSlot[]; // Issue #1078: Use shared type with base_price: string
  onBoothClick?: (slotId: number) => void;
  highlightedSlotId?: number;
}

const InteractiveBoothMap: React.FC<InteractiveBoothMapProps> = ({
  fairId,
  layouts = mockBoothLayouts,
  amenities = mockBoothAmenities,
  slots = mockSlotsWithFeatures,
  onBoothClick,
  highlightedSlotId,
}) => {
  // Accessibility (Issue #744)
  const prefersReducedMotion = usePrefersReducedMotion();
  const [focusedBoothIndex, setFocusedBoothIndex] = useState<number>(-1);
  const [announceMessage, setAnnounceMessage] = useState<string>('');

  // Mouse/touch state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const boothRefs = useRef<(SVGGElement | null)[]>([]);

  // Issue #1159: Customer-centric vendor search and filtering
  const [vendorSearchQuery, setVendorSearchQuery] = useState<string>('');
  const debouncedVendorSearchQuery = useDebounce(vendorSearchQuery, 300); // Issue #1218: 300ms debounce
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Issue #1159: Booth details modal state
  const [showBoothDetails, setShowBoothDetails] = useState(false);
  const [boothDetailsSlot, setBoothDetailsSlot] = useState<BoothMapSlot | null>(null);

  // Touch gesture state (Issue #749)
  const [pinchDistance, setPinchDistance] = useState<number | null>(null);
  const [touchHoldTimer, setTouchHoldTimer] = useState<NodeJS.Timeout | null>(null);
  const [lastTapTime, setLastTapTime] = useState<number>(0);
  const [swipeStart, setSwipeStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [showLegend, setShowLegend] = useState(false);

  // Visual feedback for zoom limits
  const [showZoomLimitFeedback, setShowZoomLimitFeedback] = useState(false);

  const viewBoxWidth = 500;
  const viewBoxHeight = 400;

  // Issue #744: Increased max zoom to 5x for low vision users
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 5;

  // Helper function to announce messages to screen readers (Issue #744)
  const announceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const announce = useCallback((message: string) => {
    setAnnounceMessage(message);
    // Clear any existing timer
    if (announceTimerRef.current) {
      clearTimeout(announceTimerRef.current);
    }
    // Clear message after announcement
    announceTimerRef.current = setTimeout(() => setAnnounceMessage(''), 1000);
  }, []);

  // Cleanup announce timer on unmount
  useEffect(() => {
    return () => {
      if (announceTimerRef.current) {
        clearTimeout(announceTimerRef.current);
      }
    };
  }, []);

  // Handle mouse wheel zoom
  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;

    // Use functional state update to avoid stale zoom values during rapid wheel events
    setZoom((prevZoom) => {
      const newZoom = Math.min(Math.max(prevZoom * delta, MIN_ZOOM), MAX_ZOOM);

      // Announce zoom level changes (Issue #744)
      if (newZoom >= MAX_ZOOM) {
        announce('Maximum zoom level reached');
      } else if (newZoom <= MIN_ZOOM) {
        announce('Minimum zoom level reached');
      }

      return newZoom;
    });
  };

  // Handle mouse down for dragging
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button === 0) { // Left click only
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers (Issue #749)

  // Calculate distance between two touch points (memoized for performance)
  const getTouchDistance = useCallback((touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Calculate midpoint between two touch points (memoized for performance)
  const getTouchMidpoint = useCallback((touch1: React.Touch, touch2: React.Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  }, []);

  // Handle touch start - supports single tap, tap-and-hold, pinch, and swipe
  const handleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    console.debug('[InteractiveBoothMap] Touch start', { touches: e.touches.length });

    // Two-finger gestures (pinch zoom, two-finger pan)
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      setPinchDistance(distance);

      const midpoint = getTouchMidpoint(touch1, touch2);
      setDragStart({ x: midpoint.x - pan.x, y: midpoint.y - pan.y });

      console.debug('[InteractiveBoothMap] Two-finger gesture detected', { distance });
    }
    // Single finger gesture (tap, tap-and-hold, swipe)
    else if (e.touches.length === 1) {
      const touch = e.touches[0];
      const currentTime = Date.now();

      // Store swipe start for gesture detection
      setSwipeStart({ x: touch.clientX, y: touch.clientY, time: currentTime });

      // Detect double-tap (within 300ms)
      if (currentTime - lastTapTime < 300) {
        e.preventDefault();
        // Double-tap: zoom in on the tapped location
        const newZoom = Math.min(zoom * 1.5, MAX_ZOOM);
        setZoom(newZoom);
        vibrateLight();
        if (newZoom >= MAX_ZOOM) announce('Maximum zoom level reached');
        console.info('[InteractiveBoothMap] Double-tap zoom', { newZoom });
        setLastTapTime(0); // Reset to prevent triple-tap
        return;
      }

      setLastTapTime(currentTime);

      // Start tap-and-hold timer (500ms)
      const holdTimer = setTimeout(() => {
        // Find the booth element that was tapped
        const target = e.target as SVGElement;
        const boothGroup = target.closest('.booth-shape');

        if (boothGroup) {
          const slotId = parseInt(boothGroup.getAttribute('data-slot-id') || '0');
          if (slotId) {
            console.info('[InteractiveBoothMap] Tap-and-hold detected', { slotId });
            setSelectedSlotId(slotId);
            vibrateSuccess(); // Haptic feedback for tap-and-hold
          }
        }
      }, 500);

      setTouchHoldTimer(holdTimer);

      // Start dragging for single-finger pan
      setIsDragging(true);
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
    }
  };

  // Handle touch move - supports pinch zoom, two-finger pan, and single-finger pan
  const handleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    // Clear tap-and-hold timer if user moves their finger
    if (touchHoldTimer) {
      clearTimeout(touchHoldTimer);
      setTouchHoldTimer(null);
    }

    // Two-finger pinch zoom and pan
    if (e.touches.length === 2 && pinchDistance !== null) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // Pinch zoom
      const newDistance = getTouchDistance(touch1, touch2);
      const delta = newDistance / pinchDistance;
      const newZoom = Math.min(Math.max(zoom * delta, MIN_ZOOM), MAX_ZOOM);

      // Trigger haptic feedback and visual indicator when reaching zoom limits
      if ((newZoom >= MAX_ZOOM && zoom < MAX_ZOOM) || (newZoom <= MIN_ZOOM && zoom > MIN_ZOOM)) {
        vibrateNotification();
        const message = newZoom >= MAX_ZOOM ? 'Maximum zoom level reached' : 'Minimum zoom level reached';
        announce(message);
        console.info('[InteractiveBoothMap] Zoom limit reached', { newZoom });
        setShowZoomLimitFeedback(true);
        setTimeout(() => setShowZoomLimitFeedback(false), 1000);
      }

      setZoom(newZoom);
      setPinchDistance(newDistance);

      // Two-finger pan
      const midpoint = getTouchMidpoint(touch1, touch2);
      setPan({
        x: midpoint.x - dragStart.x,
        y: midpoint.y - dragStart.y,
      });

      console.debug('[InteractiveBoothMap] Pinch/pan', { newZoom, pan: { x: midpoint.x - dragStart.x, y: midpoint.y - dragStart.y } });
    }
    // Single-finger pan
    else if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      setPan({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    }
  };

  // Handle touch end - detect swipe gestures and single taps
  const handleTouchEnd = (e: React.TouchEvent<SVGSVGElement>) => {
    console.debug('[InteractiveBoothMap] Touch end', { touches: e.touches.length });

    // Clear tap-and-hold timer
    if (touchHoldTimer) {
      clearTimeout(touchHoldTimer);
      setTouchHoldTimer(null);
    }

    // Detect swipe gestures
    if (swipeStart && e.touches.length === 0) {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - swipeStart.x;
      const deltaY = touch.clientY - swipeStart.y;
      const deltaTime = Date.now() - swipeStart.time;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Swipe must be > 50px, < 300ms, and mostly vertical or horizontal
      if (distance > 50 && deltaTime < 300) {
        const angle = Math.abs(Math.atan2(deltaY, deltaX) * (180 / Math.PI));

        // Vertical swipe (up/down) - control legend visibility
        if (angle > 45 && angle < 135) {
          if (deltaY < 0) {
            // Swipe up - show legend
            setShowLegend(true);
            vibrateLight();
            console.info('[InteractiveBoothMap] Swipe up - show legend');
          } else {
            // Swipe down - hide legend
            setShowLegend(false);
            vibrateLight();
            console.info('[InteractiveBoothMap] Swipe down - hide legend');
          }
        }
        // Horizontal swipe (left/right) - could be used for quick pan
        else {
          console.debug('[InteractiveBoothMap] Horizontal swipe detected', { deltaX });
        }
      }
      // Single tap (not a swipe, not a double-tap)
      else if (distance < 10 && deltaTime < 300) {
        const target = e.target as SVGElement;
        const boothGroup = target.closest('.booth-shape');

        if (boothGroup) {
          const slotId = parseInt(boothGroup.getAttribute('data-slot-id') || '0');
          if (slotId) {
            console.info('[InteractiveBoothMap] Single tap on booth', { slotId });
            setSelectedSlotId(slotId);
            vibrateLight(); // Haptic feedback for selection
            onBoothClick?.(slotId);
          }
        }
      }

      setSwipeStart(null);
    }

    // Reset pinch state
    setPinchDistance(null);
    setIsDragging(false);
  };

  // Set up touch event listeners with proper cleanup (Issue #749)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // Prevent default touch behaviors (browser pinch zoom, pull-to-refresh)
    const preventDefaultTouch = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    svg.addEventListener('touchstart', preventDefaultTouch, { passive: false });
    svg.addEventListener('touchmove', preventDefaultTouch, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', preventDefaultTouch);
      svg.removeEventListener('touchmove', preventDefaultTouch);

      // Clean up any active timers
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
      }
    };
  }, [touchHoldTimer]);

  // Handle booth click (Issue #744: Wrapped in useCallback for stable reference)
  // Defined before keyboard useEffect to avoid TDZ error
  // Issue #1159: Updated to show booth details modal
  const handleBoothClick = useCallback((slotId: number) => {
    setSelectedSlotId(slotId);

    // Find the slot to show in the modal
    const slot = slots.find(s => s.id === slotId);
    if (slot) {
      setBoothDetailsSlot(slot);
      setShowBoothDetails(true);
    }

    onBoothClick?.(slotId);
  }, [onBoothClick, slots]);

  // Enhanced keyboard navigation for accessibility (Issue #744)
  // Scoped to SVG element to avoid intercepting global shortcuts
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard events when the SVG or a booth within it has focus
      if (!svg.contains(document.activeElement)) {
        return;
      }

      const panStep = 20;
      const zoomStep = 0.1;

      switch (e.key) {
        case '+':
        case '=':
          // Zoom in
          e.preventDefault();
          setZoom((z) => {
            const newZoom = Math.min(z + zoomStep, MAX_ZOOM);
            if (newZoom >= MAX_ZOOM) {
              setShowZoomLimitFeedback(true);
              setTimeout(() => setShowZoomLimitFeedback(false), 1000);
              vibrateNotification();
              announce('Maximum zoom level reached');
            }
            return newZoom;
          });
          console.info('[InteractiveBoothMap] Keyboard: Zoom in');
          break;
        case '-':
        case '_':
          // Zoom out
          e.preventDefault();
          setZoom((z) => {
            const newZoom = Math.max(z - zoomStep, MIN_ZOOM);
            if (newZoom <= MIN_ZOOM) {
              setShowZoomLimitFeedback(true);
              setTimeout(() => setShowZoomLimitFeedback(false), 1000);
              vibrateNotification();
              announce('Minimum zoom level reached');
            }
            return newZoom;
          });
          console.info('[InteractiveBoothMap] Keyboard: Zoom out');
          break;
        case 'r':
        case 'R':
          // Reset zoom and pan
          e.preventDefault();
          setZoom(1);
          setPan({ x: 0, y: 0 });
          announce('View reset to default');
          console.info('[InteractiveBoothMap] Keyboard: Reset view');
          break;
        case 'ArrowUp':
          // Pan up or navigate booths (with Shift modifier)
          e.preventDefault();
          if (e.shiftKey && focusedBoothIndex >= 0) {
            // Navigate to previous booth
            const newIndex = Math.max(0, focusedBoothIndex - 1);
            setFocusedBoothIndex(newIndex);
            boothRefs.current[newIndex]?.focus();
            const slot = slots.find(s => s.id === layouts[newIndex]?.slot_id);
            if (slot) announce(`Booth ${slot.slot_number}`);
          } else {
            setPan((p) => ({ x: p.x, y: p.y + panStep }));
          }
          break;
        case 'ArrowDown':
          // Pan down or navigate booths (with Shift modifier)
          e.preventDefault();
          if (e.shiftKey && focusedBoothIndex >= 0) {
            // Navigate to next booth
            const newIndex = Math.min(layouts.length - 1, focusedBoothIndex + 1);
            setFocusedBoothIndex(newIndex);
            boothRefs.current[newIndex]?.focus();
            const slot = slots.find(s => s.id === layouts[newIndex]?.slot_id);
            if (slot) announce(`Booth ${slot.slot_number}`);
          } else {
            setPan((p) => ({ x: p.x, y: p.y - panStep }));
          }
          break;
        case 'ArrowLeft':
          // Pan left
          e.preventDefault();
          setPan((p) => ({ x: p.x + panStep, y: p.y }));
          break;
        case 'ArrowRight':
          // Pan right
          e.preventDefault();
          setPan((p) => ({ x: p.x - panStep, y: p.y }));
          break;
        case 'Tab':
          // Tab navigation through booths (handled by browser focus management)
          // Don't prevent default to allow natural tabbing
          break;
        case 'Enter':
        case ' ':
          // Activate focused booth
          if (focusedBoothIndex >= 0) {
            e.preventDefault();
            const layout = layouts[focusedBoothIndex];
            if (layout) {
              handleBoothClick(layout.slot_id);
              const slot = slots.find(s => s.id === layout.slot_id);
              if (slot) announce(`Selected booth ${slot.slot_number}`);
            }
          }
          break;
        case 'Escape':
          // Close selected booth details
          e.preventDefault();
          if (selectedSlotId !== null) {
            setSelectedSlotId(null);
            announce('Booth details closed');
            console.info('[InteractiveBoothMap] Keyboard: Close booth details');
          }
          break;
        case 'l':
        case 'L':
          // Toggle legend
          e.preventDefault();
          setShowLegend((s) => {
            const newState = !s;
            announce(newState ? 'Legend opened' : 'Legend closed');
            return newState;
          });
          vibrateLight();
          console.info('[InteractiveBoothMap] Keyboard: Toggle legend');
          break;
      }
    };

    // Attach to document but check focus within SVG
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [focusedBoothIndex, selectedSlotId, layouts, slots, announce, handleBoothClick]);

  // Get slot details for a layout
  const getSlotForLayout = useCallback((layout: BoothLayout): BoothMapSlot | undefined => {
    return slots.find((slot) => slot.id === layout.slot_id);
  }, [slots]);

  // Issue #1159, #1218: Filter slots based on vendor search (debounced) and product type filters
  const matchesVendorSearch = useCallback((slot: BoothMapSlot): boolean => {
    if (!debouncedVendorSearchQuery.trim()) return true;

    const query = debouncedVendorSearchQuery.toLowerCase();
    const vendorName = slot.vendor_name?.toLowerCase() || '';
    const businessName = slot.vendor_business_name?.toLowerCase() || '';

    return vendorName.includes(query) || businessName.includes(query);
  }, [debouncedVendorSearchQuery]);

  const matchesProductTypeFilter = useCallback((slot: BoothMapSlot): boolean => {
    if (selectedProductTypes.length === 0) return true;

    const slotProductTypes = slot.vendor_product_types || [];
    return selectedProductTypes.some(selectedType =>
      slotProductTypes.some(slotType =>
        slotType.toLowerCase().includes(selectedType.toLowerCase())
      )
    );
  }, [selectedProductTypes]);

  const slotMatchesFilters = useCallback((slot: BoothMapSlot): boolean => {
    return matchesVendorSearch(slot) && matchesProductTypeFilter(slot);
  }, [matchesVendorSearch, matchesProductTypeFilter]);

  // Get all unique product types from reserved slots for filter options
  const availableProductTypes = useMemo(() => {
    const types = new Set<string>();
    slots.forEach(slot => {
      if (slot.vendor_product_types) {
        slot.vendor_product_types.forEach(type => types.add(type));
      }
    });
    return Array.from(types).sort();
  }, [slots]);

  // Filter layouts to only show matching booths (Issue #1218: uses debounced search)
  const filteredLayouts = useMemo(() => {
    // If no filters active, show all layouts
    if (!debouncedVendorSearchQuery.trim() && selectedProductTypes.length === 0) {
      return layouts;
    }

    // Otherwise, filter to only matching booths
    return layouts.filter(layout => {
      const slot = getSlotForLayout(layout);
      return slot && slotMatchesFilters(slot);
    });
  }, [layouts, debouncedVendorSearchQuery, selectedProductTypes, getSlotForLayout, slotMatchesFilters]);

  // Count matching booths for results display
  const matchingBoothsCount = filteredLayouts.length;
  const totalBoothsCount = layouts.length;
  const hasActiveFilters = debouncedVendorSearchQuery.trim() || selectedProductTypes.length > 0;

  // Clear all filters
  const clearFilters = () => {
    setVendorSearchQuery('');
    setSelectedProductTypes([]);
    announce('All filters cleared');
  };

  // Issue #1218: Announce search results only after debouncing completes
  useEffect(() => {
    if (debouncedVendorSearchQuery.trim() || selectedProductTypes.length > 0) {
      announce(`Searching vendors: ${matchingBoothsCount} ${matchingBoothsCount === 1 ? 'booth' : 'booths'} found`);
    }
  }, [debouncedVendorSearchQuery, selectedProductTypes, matchingBoothsCount, announce]);

  // Issue #744: Determine booth color with high contrast mode support
  const getBoothColor = (slot: BoothMapSlot, highContrast: boolean = false): string => {
    if (highContrast) {
      // High contrast mode: black and white only
      return slot.is_available ? '#FFFFFF' : '#000000';
    }

    // Normal color mode with WCAG AA contrast
    if (!slot.is_available) return '#DC2626'; // Red - Reserved (7.5:1 contrast)
    if (slot.corner_booth) return '#D97706'; // Amber - Corner (6.8:1 contrast)
    if (slot.high_traffic_area) return '#7C3AED'; // Purple - High traffic (4.5:1 contrast)
    if (slot.near_entrance) return '#0891B2'; // Cyan - Near entrance (5.2:1 contrast)
    return '#059669'; // Green - Available (7.2:1 contrast)
  };

  // Issue #744: Get SVG pattern ID for booth states (visual differentiation beyond color)
  const getBoothPattern = (slot: BoothMapSlot): string | null => {
    if (!slot.is_available) return 'pattern-reserved'; // Diagonal stripes
    if (slot.corner_booth) return 'pattern-corner'; // Dots
    if (slot.high_traffic_area) return 'pattern-high-traffic'; // Horizontal lines
    return null; // Solid fill for available booths
  };

  // Issue #744: Detect high contrast mode preference
  const [highContrastMode, setHighContrastMode] = useState(false);

  useEffect(() => {
    // Detect Windows High Contrast Mode or forced-colors (Issue #744)
    // Guard against SSR and older browsers (mirrors usePrefersReducedMotion pattern)
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-contrast: more), (forced-colors: active)');

    // Set initial value
    setHighContrastMode(mediaQuery.matches);

    // Listen for changes
    const handler = (event: MediaQueryListEvent | MediaQueryList) => {
      setHighContrastMode(event.matches);
    };

    // Use addEventListener if supported (modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Fallback for older browsers (addListener/removeListener are deprecated)
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  return (
    <div className="interactive-booth-map relative bg-gray-50 rounded-lg border-2 border-gray-200 overflow-hidden">
      {/* Screen reader instructions (Issue #744) */}
      <div id="booth-map-instructions" className="sr-only">
        Interactive booth map. Use Tab to navigate booths, Enter or Space to select, Escape to close details.
        Use plus and minus keys to zoom, arrow keys to pan the map, R to reset view, L to toggle legend.
        Shift plus arrow keys to navigate between booths.
        Use the vendor search to filter booths by vendor name or product type.
      </div>

      {/* Live region for screen reader announcements (Issue #744) */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announceMessage}
      </div>

      {/* Issue #1159: Vendor Search and Filter Controls (Customer-centric feature) */}
      <div className="bg-white border-b-2 border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          {/* Search input */}
          <div className="flex-1 max-w-md">
            <label htmlFor="vendor-search" className="sr-only">
              Search vendors by name
            </label>
            <input
              id="vendor-search"
              type="text"
              placeholder="Search vendors by name..."
              value={vendorSearchQuery}
              onChange={(e) => setVendorSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-transparent"
              aria-label="Search vendors by name or business name"
              aria-describedby="search-results-count"
              data-testid="vendor-search-input"
            />
          </div>

          {/* Filter toggle button (mobile-friendly) */}
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-dusty-teal focus:outline-none flex items-center gap-2"
              aria-label={showFilters ? 'Hide product filters' : 'Show product filters'}
              aria-expanded={showFilters}
              data-testid="toggle-filters-button"
            >
              <span className="text-sm font-medium">Filters</span>
              {selectedProductTypes.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-primary rounded-full">
                  {selectedProductTypes.length}
                </span>
              )}
            </button>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 focus:ring-2 focus:ring-dusty-teal focus:outline-none rounded-lg"
                aria-label="Clear all filters"
                data-testid="clear-filters-button"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Product type filters (expandable) */}
        {showFilters && availableProductTypes.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-testid="product-type-filters">
            <fieldset>
              <legend className="text-sm font-medium text-gray-700 mb-3">
                Filter by Product Type
              </legend>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {availableProductTypes.map(productType => (
                  <label
                    key={productType}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded"
                    data-testid={`product-filter-${productType.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProductTypes.includes(productType)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProductTypes([...selectedProductTypes, productType]);
                          announce(`${productType} filter added`);
                        } else {
                          setSelectedProductTypes(selectedProductTypes.filter(t => t !== productType));
                          announce(`${productType} filter removed`);
                        }
                      }}
                      className="rounded border-gray-300 text-secondary focus:ring-dusty-teal"
                      aria-label={`Filter by ${productType}`}
                    />
                    <span className="text-sm text-gray-700">{productType}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}

        {/* Results count */}
        <div
          id="search-results-count"
          className="mt-3 text-sm text-gray-600"
          aria-live="polite"
          aria-atomic="true"
          data-testid="search-results-count"
        >
          {hasActiveFilters ? (
            <span>
              Showing <strong>{matchingBoothsCount}</strong> of {totalBoothsCount} booths
            </span>
          ) : (
            <span>
              Showing all <strong>{totalBoothsCount}</strong> booths
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-md p-2 space-y-2">
        <button
          onClick={() => {
            setZoom((z) => {
              const newZoom = Math.min(z * 1.2, MAX_ZOOM);
              if (newZoom >= MAX_ZOOM) announce('Maximum zoom level reached');
              return newZoom;
            });
          }}
          className="block w-full px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusty-teal"
          aria-label="Zoom in on booth map"
          data-testid="zoom-in-button"
        >
          Zoom In
        </button>
        <button
          onClick={() => {
            setZoom((z) => {
              const newZoom = Math.max(z * 0.8, MIN_ZOOM);
              if (newZoom <= MIN_ZOOM) announce('Minimum zoom level reached');
              return newZoom;
            });
          }}
          className="block w-full px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusty-teal"
          aria-label="Zoom out on booth map"
          data-testid="zoom-out-button"
        >
          Zoom Out
        </button>
        <button
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
            announce('View reset to default');
          }}
          className="block w-full px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          aria-label="Reset booth map zoom and position"
          data-testid="reset-view-button"
        >
          Reset
        </button>
      </div>

      {/* SVG Map (Issue #744: Enhanced with ARIA attributes and accessibility features) */}
      {/* Using role="group" instead of "application" or "img" to preserve screen reader shortcuts */}
      {/* while allowing focusable booth buttons without triggering nested-interactive violations */}
      {/* SVG itself is not focusable - individual booth buttons handle keyboard interaction */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        className="w-full h-[600px] cursor-grab active:cursor-grabbing"
        role="group"
        aria-label="Interactive booth map for fair with zoom, pan, and booth selection controls"
        aria-describedby="booth-map-instructions"
        data-testid="booth-map-svg"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none', // Prevent browser default touch behaviors
        }}
      >
        <defs>
          {/* Grid background */}
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#E5E7EB" strokeWidth="1" />
          </pattern>

          {/* SVG Patterns for booth states (Issue #744: Visual differentiation beyond color) */}
          {/* Reserved booths: Diagonal stripes */}
          <pattern id="pattern-reserved" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="4" height="8" fill="#DC2626" />
            <rect x="4" width="4" height="8" fill="#F87171" />
          </pattern>

          {/* Corner booths: Dots pattern */}
          <pattern id="pattern-corner" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="5" cy="5" r="2" fill="#D97706" />
          </pattern>

          {/* High traffic area: Horizontal lines */}
          <pattern id="pattern-high-traffic" width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="4" fill="#7C3AED" />
            <rect y="4" width="8" height="4" fill="#A78BFA" />
          </pattern>
        </defs>

        {/* Apply zoom and pan transformations */}
        <g transform={`translate(${pan.x / 2}, ${pan.y / 2}) scale(${zoom})`}>
          <rect width={viewBoxWidth} height={viewBoxHeight} fill="url(#grid)" />

          {/* Render booths (Issue #744: Enhanced with ARIA attributes and focus management) */}
          {/* Issue #1159: Render all booths but dim non-matching ones when filters are active */}
          {layouts.map((layout, index) => {
            const slot = getSlotForLayout(layout);
            if (!slot) return null;

            const color = getBoothColor(slot, highContrastMode);
            const pattern = getBoothPattern(slot);
            const isHighlighted = highlightedSlotId === layout.slot_id || selectedSlotId === layout.slot_id;
            const isFocused = focusedBoothIndex === index;

            // Issue #1159: Dim booths that don't match current filters
            const matchesFilters = slotMatchesFilters(slot);
            const isDimmed = hasActiveFilters && !matchesFilters;

            // Issue #744: In high-contrast mode, prioritize solid colors over patterns
            // Patterns may not render properly in forced-colors mode
            const fillValue = highContrastMode ? color : (pattern ? `url(#${pattern})` : color);

            // Build accessible label for screen readers
            const ariaLabel = [
              `Booth ${slot.slot_number}`,
              slot.dimensions ?? 'Dimensions not specified',
              `Price: ${formatCurrency(String(slot.base_price))}`,
              slot.is_available ? 'Available' : 'Reserved',
              slot.corner_booth ? 'Corner booth' : '',
              slot.high_traffic_area ? 'High traffic area' : '',
              slot.near_entrance ? 'Near entrance' : '',
              slot.has_power ? 'Power available' : '',
              slot.estimated_foot_traffic ? `Average ${slot.estimated_foot_traffic} visitors per day` : '',
            ].filter(Boolean).join(', ');

            return (
              <g
                key={layout.id}
                ref={el => boothRefs.current[index] = el}
                transform={`translate(${layout.x_coord}, ${layout.y_coord}) rotate(${layout.rotation})`}
                onClick={() => handleBoothClick(layout.slot_id)}
                onFocus={() => {
                  setFocusedBoothIndex(index);
                  announce(`Focused on booth ${slot.slot_number}`);
                }}
                onBlur={() => {
                  if (focusedBoothIndex === index) setFocusedBoothIndex(-1);
                }}
                className="booth-shape cursor-pointer"
                data-slot-id={layout.slot_id}
                role="button"
                aria-label={ariaLabel}
                aria-pressed={isHighlighted}
                tabIndex={0}
                focusable="true"
              >
                {/* Booth rectangle with pattern fill (disabled in high-contrast mode) */}
                {/* Issue #1159: Apply dimming to non-matching booths when filters are active */}
                <rect
                  width={layout.width}
                  height={layout.height}
                  fill={fillValue}
                  stroke={
                    isFocused ? '#3B82F6' : // Blue focus indicator
                    isHighlighted ? '#1F2937' : // Dark gray when selected
                    highContrastMode ? '#000000' : // Black in high contrast
                    matchesFilters && hasActiveFilters ? '#10B981' : // Green border for matching booths
                    '#374151' // Default gray
                  }
                  strokeWidth={
                    isFocused ? 4 : // Thicker stroke for keyboard focus
                    isHighlighted ? 3 :
                    matchesFilters && hasActiveFilters ? 2.5 : // Slightly thicker for matching booths
                    highContrastMode ? 3 : // Thicker stroke in high contrast
                    1
                  }
                  opacity={
                    isDimmed ? 0.2 : // Dim non-matching booths
                    highContrastMode ? 1 :
                    0.8
                  }
                  className={prefersReducedMotion ? '' : 'transition-all hover:opacity-100'}
                  aria-hidden="true"
                />

                {/* Booth label (Issue #744: Scaled with zoom for readability) */}
                {/* Issue #1159: Dim text for non-matching booths */}
                <text
                  x={layout.width / 2}
                  y={layout.height / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={Math.max(14, 14 / zoom)} // Scale text inversely to zoom
                  fontWeight="bold"
                  fill={highContrastMode && !slot.is_available ? '#FFFFFF' : 'white'}
                  stroke={highContrastMode ? '#000000' : 'none'}
                  strokeWidth={highContrastMode ? 0.5 : 0}
                  opacity={isDimmed ? 0.3 : 1}
                  className="pointer-events-none"
                  aria-hidden="true"
                >
                  {slot.slot_number}
                </text>

                {/* Power indicator */}
                {slot.has_power && (
                  <text
                    x={layout.width - 10}
                    y={15}
                    fontSize={Math.max(12, 12 / zoom)}
                    className="pointer-events-none"
                    aria-hidden="true"
                  >
                    ⚡
                  </text>
                )}
              </g>
            );
          })}

          {/* Render amenities */}
          {amenities.map((amenity) => (
            <AmenityMarker
              key={amenity.id}
              name={amenity.name}
              type={amenity.type}
              x={amenity.x_coord}
              y={amenity.y_coord}
              icon={amenity.icon}
              scale={zoom}
            />
          ))}
        </g>
      </svg>

      {/* Selected booth info (Issue #744: Enhanced accessibility) */}
      {selectedSlotId && (
        <div
          role="dialog"
          aria-labelledby="booth-details-title"
          aria-modal="false"
          className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 max-w-xs"
          data-testid="booth-details-panel"
        >
          {(() => {
            const slot = slots.find((s) => s.id === selectedSlotId);
            if (!slot) return null;

            return (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h4 id="booth-details-title" className="text-lg font-bold text-gray-900">
                    Booth {slot.slot_number}
                  </h4>
                  <button
                    onClick={() => {
                      setSelectedSlotId(null);
                      announce('Booth details closed');
                    }}
                    className="text-gray-500 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusty-teal rounded"
                    aria-label={`Close booth ${slot.slot_number} details`}
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Dimensions:</strong> {slot.dimensions ?? '—'}</p>
                  <p><strong>Price:</strong> {formatCurrency(String(slot.base_price))} {/* Issue #905: Decimal.js formatting */}</p>
                  <p><strong>Type:</strong> {slot.slot_type}</p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={slot.is_available ? 'text-green-700' : 'text-red-700'}>
                      {slot.is_available ? 'Available' : 'Reserved'}
                    </span>
                  </p>
                  {slot.has_power && <p><span aria-label="Power available">⚡</span> Power available</p>}
                  {slot.corner_booth && <p><span aria-label="Corner booth">📍</span> Corner booth</p>}
                  {slot.high_traffic_area && <p><span aria-label="High traffic area">🚶</span> High traffic area</p>}
                  {slot.near_entrance && <p><span aria-label="Near entrance">🚪</span> Near entrance</p>}
                  {slot.estimated_foot_traffic && (
                    <p><span aria-label="Average visitors">👥</span> ~{slot.estimated_foot_traffic} visitors/day</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Swipeable Legend (Issue #749, #744) */}
      {showLegend && (
        <div
          role="region"
          aria-labelledby="legend-title"
          className={`absolute top-4 left-4 z-10 max-w-xs ${prefersReducedMotion ? '' : 'animate-slide-down'}`}
        >
          <div className="flex items-center justify-between bg-white rounded-t-lg px-4 py-2 border-b">
            <h3 id="legend-title" className="text-sm font-bold text-gray-700">Map Legend</h3>
            <button
              onClick={() => {
                setShowLegend(false);
                announce('Legend closed');
                vibrateLight();
              }}
              className="text-gray-500 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusty-teal rounded"
              aria-label="Close map legend"
            >
              ✕
            </button>
          </div>
          <BoothMapLegend />
        </div>
      )}

      {/* Legend toggle button (mobile-friendly, Issue #744) */}
      {!showLegend && (
        <button
          onClick={() => {
            setShowLegend(true);
            announce('Legend opened');
            vibrateLight();
          }}
          className="absolute bottom-4 right-4 z-10 bg-primary text-white rounded-full p-3 shadow-lg hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusty-teal"
          aria-label="Show map legend"
        >
          <span role="img" aria-label="Information">ℹ️</span>
        </button>
      )}

      {/* Zoom limit feedback (Issue #749, #744 - UX enhancement with accessibility) */}
      {showZoomLimitFeedback && (
        <div
          role="status"
          aria-live="polite"
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-gray-900 bg-opacity-75 text-white px-6 py-3 rounded-lg shadow-lg ${prefersReducedMotion ? '' : 'animate-fade-in'}`}
        >
          <p className="text-sm font-semibold">
            {zoom >= MAX_ZOOM ? 'Maximum zoom reached' : 'Minimum zoom reached'}
          </p>
        </div>
      )}

      {/* Issue #1159: Booth Details Modal - Customer-centric booth information */}
      <BoothDetailsModal
        slot={boothDetailsSlot}
        isOpen={showBoothDetails}
        onClose={() => {
          setShowBoothDetails(false);
          setBoothDetailsSlot(null);
        }}
        fairId={fairId}
      />
    </div>
  );
};

export default InteractiveBoothMap;
